import {useState, useEffect} from 'react'
import {KTSVG} from '../../../../../../../_metronic/helpers'
import axios from 'axios'
import toaster from '../../../../../../../Utils/toaster'
import {useAuth} from '../../../../../../modules/auth'
import {useParams} from 'react-router-dom'
import {useQueryResponse} from '../../core/QueryResponseProvider'
import Tooltip from 'react-bootstrap/Tooltip'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'

const API_URL = process.env.REACT_APP_API_URL

const ListToolbar = () => {
  const {currentUser} = useAuth()
  const {id: customerCode} = useParams<{id: string}>()
  const {refetch} = useQueryResponse()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [masterIvrList, setMasterIvrList] = useState<any[]>([])
  const [selectedIvr, setSelectedIvr] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingIvrList, setIsLoadingIvrList] = useState(false)

  // Handle modal body class for proper animation
  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add('modal-open')
    } else {
      document.body.classList.remove('modal-open')
    }
    
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [isModalOpen])

  const fetchMasterIvrData = async () => {
    setIsLoadingIvrList(true)
    try {
      // Fetch both master IVR data and current customer's IVR data
      const [masterResponse, customerResponse] = await Promise.all([
        axios.get(`${API_URL}/master/ivr-numbers`),
        customerCode ? axios.get(`${API_URL}/userhasivr/customer/${customerCode}`) : Promise.resolve({data: {data: []}})
      ])
      
      const masterIvrData = masterResponse.data.data || []
      const customerIvrData = customerResponse.data.data || []
      
      // Get the IVR codes that are already assigned to this customer
      const assignedIvrCodes = customerIvrData.map((ivr: any) => ivr.ivrCode)
      
      // Filter out IVR numbers that are already assigned
      const availableIvrData = masterIvrData.filter((ivr: any) => {
        const ivrCode = ivr.code || ivr.ivrCode
        return !assignedIvrCodes.includes(ivrCode)
      })
      
      setMasterIvrList(availableIvrData)
    } catch (error) {
      console.error('Error fetching master IVR data:', error)
      toaster('error', 'Failed to load IVR numbers')
      setMasterIvrList([])
    } finally {
      setIsLoadingIvrList(false)
    }
  }

  const handleAddIvr = () => {
    setIsModalOpen(true)
    setSelectedIvr('') // Reset selection
    fetchMasterIvrData() // Fetch master IVR data when modal opens
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedIvr('') // Reset selection when closing
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleCloseModal()
    }
  }

  const handleModalSubmit = async () => {
    if (!selectedIvr) {
      toaster('error', 'Please select an IVR number')
      return
    }

    if (!customerCode) {
      toaster('error', 'Customer code is required')
      return
    }

    setIsSubmitting(true)
    try {
      // Call the API to add IVR to customer
      const response = await axios.post(`${API_URL}/userhasivr/add`, {
        customerCode: parseInt(customerCode),
        ivrCode: selectedIvr
      })
      
      toaster('success', 'IVR number added successfully')
      handleCloseModal()
      
      // Refresh the table data by triggering a refetch
      refetch()
      
    } catch (error) {
      console.error('Error adding IVR:', error)
      toaster('error', 'Failed to add IVR number')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <div className='d-flex justify-content-end' data-kt-user-table-toolbar='base'>
        <OverlayTrigger
          placement='top'
          overlay={<Tooltip id='tooltip-filter'>Add IVR Number</Tooltip>}
        >
          <div>
            <button
              type='button'
              className='btn btn-sm btn-primary btn-icon'
              onClick={handleAddIvr}
            >
              <i className='bi bi-plus fs-2'></i>
            </button>
          </div>
        </OverlayTrigger>
      </div>

      {/* Add IVR Modal */}
      {isModalOpen && (
        <>
          <div
            className='modal fade show d-block'
            id='kt_modal_add_ivr'
            role='dialog'
            tabIndex={-1}
            aria-modal='true'
            onClick={handleBackdropClick}
          >
            <div className='modal-dialog modal-dialog-centered mw-650px'>
              <div className='modal-content'>
                <div className='modal-header py-3'>
                  <h2 className='fw-semibolder fs-3'>Add IVR Number</h2>
                  <div
                    className='btn btn-icon btn-sm btn-active-icon-primary'
                    onClick={handleCloseModal}
                    style={{cursor: 'pointer'}}
                  >
                    <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
                  </div>
                </div>
                <div className='modal-body scroll-y'>
                  <div className='mb-5'>
                    <label className='form-label fw-bold fs-6 mb-2'>Select IVR Number</label>
                    {isLoadingIvrList ? (
                      <div className='d-flex align-items-center'>
                        <div className='spinner-border spinner-border-sm me-2'></div>
                        <span>Loading available IVR numbers...</span>
                      </div>
                    ) : (
                      <select
                        className='form-select form-select-solid'
                        value={selectedIvr}
                        onChange={(e) => setSelectedIvr(e.target.value)}
                        disabled={masterIvrList.length === 0}
                      >
                        <option value=''>
                          {masterIvrList.length === 0 ? 'All IVR numbers are already assigned' : 'Choose an IVR number...'}
                        </option>
                        {masterIvrList.map((ivr) => (
                          <option key={ivr.code || ivr.id} value={ivr.code || ivr.id}>
                            {ivr.value || ivr.ivrValue} ({ivr.code || ivr.ivrCode})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
                <div className='modal-footer'>
                  <button
                    type='button'
                    className='btn btn-light'
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </button>
                  <button
                    type='button'
                    className='btn btn-primary'
                    onClick={handleModalSubmit}
                    disabled={isSubmitting || isLoadingIvrList || masterIvrList.length === 0}
                  >
                    {isSubmitting ? (
                      <>
                        <span className='spinner-border spinner-border-sm align-middle me-2'></span>
                        Adding...
                      </>
                    ) : (
                      'Add IVR Number'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className='modal-backdrop fade show'></div>
        </>
      )}
    </>
  )
}

export {ListToolbar}
